import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  Grid,
  Link as MuiLink,
  Table as MuiTable,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { itemsClient } from '../../api/client';
import { QueryInventoryDetail, QueryInventoryItem } from '../../api/schema';
import { CategoryIcon, ProductThumbnail } from '../../component/categoryIcon/CategoryIcon';
import { ConfirmDialog } from '../../component/confirmDialog/ConfirmDialog';
import KPI from '../../component/kpi/KPI';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import { getInventory, getInventoryById, inventoryFetching } from '../../redux/slices/inventory/inventorySlice';
import { getUserAbilities } from '../../redux/slices/users/usersSlice';
import { useDispatch } from '../../redux/store';
import { Card } from '../../styled/components';
import { savedWhen } from './Inventories';

const SCAN_STATUS_LOST = 4;
const SCAN_STATUS_RETIRED = 7;

type Kpi = 'expected' | 'seen' | 'missing' | 'cleared';
type Side = 'all' | Kpi;

// The tiles an item counts toward: a listed item is Expected and either Seen or
// Missing; a seen item that was not listed here was Cleared to this site.
const KPIS: { key: Kpi; label: string; counts: (item: QueryInventoryItem) => boolean }[] = [
  { key: 'expected', label: 'Expected', counts: (item) => !!item.listed },
  { key: 'seen', label: 'Seen', counts: (item) => !!item.seen && !!item.listed },
  { key: 'missing', label: 'Missing', counts: (item) => !item.seen },
  { key: 'cleared', label: 'Cleared', counts: (item) => !!item.seen && !item.listed },
];
type Grouping = 'category' | 'product';

// What a seen item that was not listed here came from.
const PRIOR: Record<string, string> = {
  home: 'At home here',
  other_home: 'Another home site',
  vehicle: 'Vehicle',
  job_site: 'Job site',
  missing: 'Missing',
  lost: 'Lost',
  retired: 'Retired',
  never_seen: 'Never seen',
  unknown: 'Unknown',
};

const priorText = (item: QueryInventoryItem): string => {
  const where = PRIOR[item.prior_location ?? 'unknown'] ?? item.prior_location ?? '';
  const detail = item.prior_location === 'vehicle' ? item.prior_vehicle_name : item.prior_site_name;
  return detail ? `${where} · ${detail}` : where;
};

type Group = { key: string; name: string; category?: string; items: QueryInventoryItem[] };

// Groups biggest first, "Other" last; on Missing that puts the most missing first.
const groupItems = (items: QueryInventoryItem[], grouping: Grouping): Group[] => {
  const groups = new Map<string, Group>();
  items.forEach((item) => {
    const category = item.category_name || 'Other';
    const key = grouping === 'category' ? category : `${category}|${item.product_name}`;
    const group = groups.get(key) ?? { key, name: grouping === 'category' ? category : item.product_name || 'Unnamed product', category, items: [] };
    group.items.push(item);
    groups.set(key, group);
  });
  return Array.from(groups.values()).sort((a, b) => {
    if ((a.name === 'Other') !== (b.name === 'Other')) {
      return a.name === 'Other' ? 1 : -1;
    }
    return b.items.length - a.items.length || a.name.localeCompare(b.name);
  });
};

const EMPTY: Record<Side, string> = {
  all: 'Nothing was listed or seen.',
  expected: 'Nothing was listed at this site.',
  seen: 'None of the listed items was seen.',
  missing: 'Everything listed at this site was seen.',
  cleared: 'Nothing from elsewhere was cleared here.',
};

// Workbook with every field: a Summary sheet (the inventory and its tile counts),
// an Items sheet (every seen and not seen item, with its tile) and Unknown tags.
const exportInventory = (inventory: QueryInventoryDetail): void => {
  const expected = (inventory.seen_count ?? 0) - (inventory.unexpected_count ?? 0) + (inventory.not_seen_count ?? 0);
  const summary = [
    ['Site', inventory.site_name],
    ['User', inventory.user_name],
    ['Status', inventory.status],
    ['Started', savedWhen(inventory.started_at)],
    ['Updated', savedWhen(inventory.updated_at)],
    ['Saved', savedWhen(inventory.saved_at)],
    ['Expected', expected],
    ['Seen', (inventory.seen_count ?? 0) - (inventory.unexpected_count ?? 0)],
    ['Missing', inventory.not_seen_count],
    ['Cleared', inventory.unexpected_count],
    ['Unknown tags', inventory.unknown_tag_count],
  ];
  const itemRow = (item: QueryInventoryItem) => ({
    Tile: item.seen ? (item.listed ? 'Seen' : 'Cleared') : 'Missing',
    Identifier: item.identifier,
    Product: item.product_name,
    Category: item.category_name,
    Seen: item.seen ? 'Yes' : 'No',
    Listed: item.listed ? 'Yes' : 'No',
    'Came from': item.listed ? '' : priorText(item),
    'Prior location': item.prior_location,
    'Prior status': item.prior_status,
    'Prior site': item.prior_site_name,
    'Prior vehicle': item.prior_vehicle_name,
    Reads: item.reads,
    RSSI: item.rssi,
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([...(inventory.seen ?? []), ...(inventory.not_seen ?? [])].map(itemRow)), 'Items');
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(
      (inventory.unknown_tags ?? []).map((tag) => ({ RFID: tag.rfid, Reads: tag.reads, RSSI: tag.rssi })),
      {
        header: ['RFID', 'Reads', 'RSSI'],
      },
    ),
    'Unknown tags',
  );
  const when = inventory.saved_at ? DateTime.fromISO(inventory.saved_at).toFormat('yyyy-MM-dd-HH-mm') : 'draft';
  const site = (inventory.site_name ?? 'site').replace(/[^A-Za-z0-9]+/g, '-');
  XLSX.writeFile(wb, `inventory-${site}-${when}.xlsx`);
};

// Icon and name, for a group or an item cell.
const Named = ({ icon, children }: { icon: ReactNode; children: ReactNode }): ReactElement => (
  <Stack direction="row" spacing={1} alignItems="center">
    {icon}
    <span>{children}</span>
  </Stack>
);

// One saved inventory: tiles on top (Expected, Seen, Missing, Cleared) and the
// same counts as columns of a tree by category or by product; each group opens
// its items, each item its page. A tile narrows the tree to its items (click it
// again for all). Columns that nothing in the list can fill are left out.
export const InventoryDetails = (): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const inventory = useSelector(getInventoryById(id));
  const fetching = useSelector(inventoryFetching);
  const abilities = useSelector(getUserAbilities);
  const [side, setSide] = useState<Side>('all');
  const [grouping, setGrouping] = useState<Grouping>('category');
  const [open, setOpen] = useState<string[]>([]);
  // Missing items ticked to be marked Lost (Admin / Supervisor), and the ones marked from this page.
  const [selected, setSelected] = useState<string[]>([]);
  const [markedLost, setMarkedLost] = useState<string[]>([]);
  const [confirmLost, setConfirmLost] = useState(false);
  const [marking, setMarking] = useState(false);
  // Lost items found again at this site since the inventory was saved.
  const [foundSince, setFoundSince] = useState<number | undefined>();

  useEffect(() => {
    dispatch(getInventory(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (inventory?.saved_at && inventory.site_id) {
      itemsClient
        .itemsFound(inventory.saved_at, undefined, inventory.site_id, 0, 1)
        .then((response) => setFoundSince(response.data.record_count ?? 0))
        .catch(() => setFoundSince(undefined));
    }
  }, [inventory?.saved_at, inventory?.site_id]);

  const canMarkLost = abilities.can('CRUD') && side === 'missing';
  // A missing item can be marked Lost unless it already was (at the time, or from this page) or is retired.
  const markable = useCallback(
    (item: QueryInventoryItem) =>
      !item.seen && item.prior_status !== SCAN_STATUS_LOST && item.prior_status !== SCAN_STATUS_RETIRED && !markedLost.includes(item.item_id ?? ''),
    [markedLost],
  );
  const markLost = async () => {
    if (!selected.length) {
      return;
    }
    setMarking(true);
    try {
      const note = `Not seen in the inventory of ${inventory?.site_name ?? 'this site'} on ${savedWhen(inventory?.saved_at)}`;
      const response = await itemsClient.itemsChangeStatus({ item_ids: selected, action: 'lost', note });
      setMarkedLost((ids) => [...ids, ...(response.data.result?.changed ?? [])]);
      setSelected([]);
      setConfirmLost(false);
    } finally {
      setMarking(false);
    }
  };

  const lists = useMemo(() => {
    const seen = inventory?.seen ?? [];
    const missing = inventory?.not_seen ?? [];
    const seenListed = seen.filter((item) => item.listed);
    return {
      all: [...seen, ...missing],
      expected: [...seenListed, ...missing],
      seen: seenListed,
      missing,
      cleared: seen.filter((item) => !item.listed),
    };
  }, [inventory]);

  const tiles: { side?: Kpi; title: string; amount?: number }[] = [
    {
      side: 'expected',
      title: 'Expected',
      amount: inventory && (inventory.seen_count ?? 0) - (inventory.unexpected_count ?? 0) + (inventory.not_seen_count ?? 0),
    },
    { side: 'seen', title: 'Seen', amount: inventory && (inventory.seen_count ?? 0) - (inventory.unexpected_count ?? 0) },
    { side: 'missing', title: 'Missing', amount: inventory?.not_seen_count },
    { side: 'cleared', title: 'Cleared', amount: inventory?.unexpected_count },
    // Lost items seen again at this site since the save; opens the Found list.
    { title: 'Found', amount: foundSince },
  ];
  const foundLink = inventory?.saved_at
    ? `/tools/inventory?siteId=${inventory.site_id}&from=${DateTime.fromISO(inventory.saved_at).toFormat('yyyy-MM-dd')}#found`
    : '/tools/inventory#found';

  const items = lists[side];
  const groups = useMemo(() => groupItems(items, grouping), [items, grouping]);
  // Only columns something in the list can fill; one count column per tile.
  const columns = useMemo(
    () => ({
      category: grouping === 'product',
      cameFrom: items.some((item) => !item.listed),
      reads: items.some((item) => item.reads != null),
      kpis: KPIS.filter((kpi) => items.some(kpi.counts)),
    }),
    [items, grouping],
  );
  const columnCount = 1 + Number(canMarkLost) + Number(columns.category) + Number(columns.cameFrom) + Number(columns.reads) + columns.kpis.length;
  const markableItems = canMarkLost ? items.filter(markable) : [];
  const toggleSelected = (itemId: string) => setSelected((ids) => (ids.includes(itemId) ? ids.filter((i) => i !== itemId) : [...ids, itemId]));

  // A category opens its products; a product opens its items.
  const subgroups = (group: Group): Group[] => (grouping === 'category' ? groupItems(group.items, 'product') : []);
  const toggle = (key: string) => setOpen((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));
  // Clicking the selected tile again goes back to everything.
  const choose = (next: Side) => {
    setSide((current) => (current === next ? 'all' : next));
    setOpen([]);
  };

  const itemRows = (list: QueryInventoryItem[], indent: number) =>
    list.map((item) => (
      <TableRow key={item.item_id} hover>
        {canMarkLost && (
          <TableCell padding="checkbox">
            {markedLost.includes(item.item_id ?? '') ? (
              <Chip size="small" color="error" variant="outlined" label="Lost" />
            ) : (
              <Checkbox
                size="small"
                checked={selected.includes(item.item_id ?? '')}
                disabled={!markable(item)}
                onChange={() => toggleSelected(item.item_id ?? '')}
                inputProps={{ 'aria-label': `Mark ${item.identifier} lost` }}
              />
            )}
          </TableCell>
        )}
        <TableCell sx={{ pl: indent }}>
          <Named icon={<ProductThumbnail imageKey={item.image_key} category={item.category_name} />}>
            <MuiLink component={Link} to={`/tools/items/${item.item_id}/details`}>
              {item.identifier}
            </MuiLink>
          </Named>
        </TableCell>
        {columns.category && <TableCell />}
        {columns.cameFrom && <TableCell>{item.listed ? '' : <Chip size="small" color="warning" variant="outlined" label={priorText(item)} />}</TableCell>}
        {columns.reads && <TableCell align="right">{item.reads ?? ''}</TableCell>}
        {columns.kpis.map((kpi) => (
          <TableCell key={kpi.key} align="right">
            {kpi.counts(item) ? '✓' : ''}
          </TableCell>
        ))}
      </TableRow>
    ));

  // Categories get their icon; products their photo (the category icon if none).
  const groupIcon = (group: Group, isCategory: boolean) =>
    isCategory ? (
      <CategoryIcon category={group.category} />
    ) : (
      <ProductThumbnail imageKey={group.items.find((item) => item.image_key)?.image_key} category={group.category} />
    );

  const groupRow = (group: Group, indent: number, isCategory: boolean, bold: boolean) => {
    const unlisted = group.items.filter((item) => !item.listed).length;
    return (
      <TableRow key={`g-${group.key}`} hover onClick={() => toggle(group.key)} sx={{ cursor: 'pointer' }}>
        {canMarkLost && <TableCell padding="checkbox" />}
        <TableCell sx={{ pl: indent, fontWeight: bold ? 600 : 400 }}>
          <Named icon={groupIcon(group, isCategory)}>
            {open.includes(group.key) ? '▾' : '▸'} {group.name}
          </Named>
        </TableCell>
        {columns.category && (
          <TableCell>
            <Named icon={<CategoryIcon category={group.category} />}>{group.category}</Named>
          </TableCell>
        )}
        {columns.cameFrom && <TableCell>{unlisted ? `${unlisted} cleared` : ''}</TableCell>}
        {columns.reads && <TableCell />}
        {columns.kpis.map((kpi) => (
          <TableCell key={kpi.key} align="right" sx={{ fontWeight: bold ? 600 : 400 }}>
            {group.items.filter(kpi.counts).length || ''}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  return (
    <>
      {fetching && <LinearProgress />}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        {tiles.map((tile) => (
          <Grid item xs={6} sm={4} md={2} key={tile.title}>
            <Box
              data-testid={`inventory-tile-${tile.side ?? 'found'}`}
              onClick={tile.side ? () => choose(tile.side as Kpi) : () => navigate(foundLink)}
              sx={{
                height: '100%',
                borderRadius: 1,
                cursor: 'pointer',
                outline: tile.side === side ? '2px solid' : 'none',
                outlineColor: 'primary.main',
              }}
            >
              <KPI
                title={tile.title}
                data={[{ amount: tile.amount ?? '—', color: tile.side === side ? 'blue' : 'black' }]}
                fetching={fetching && !inventory}
                dense
              />
            </Box>
          </Grid>
        ))}
      </Grid>
      <Card>
        <Box sx={{ p: 2 }}>
          <Breadcrumbs sx={{ mb: 1 }}>
            <MuiLink component={Link} to="/tools/inventory">
              Inventory
            </MuiLink>
            <Typography color="text.primary">{savedWhen(inventory?.saved_at)}</Typography>
          </Breadcrumbs>
          <Typography variant="h5">
            {inventory?.site_name} · {savedWhen(inventory?.saved_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            By {inventory?.user_name} · {inventory?.unknown_tag_count ?? 0} unknown tags
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <ToggleButtonGroup size="small" exclusive value={grouping} onChange={(_, v) => v && (setGrouping(v), setOpen([]))}>
              <ToggleButton value="category">By category</ToggleButton>
              <ToggleButton value="product">By product</ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row" spacing={2} alignItems="center">
              {canMarkLost && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={!selected.length || marking}
                  onClick={() => setConfirmLost(true)}
                  data-testid="inventory-mark-lost"
                >
                  Mark {selected.length || ''} as Lost
                </Button>
              )}
              <Button variant="outlined" color="secondary" disabled={!inventory} onClick={() => inventory && exportInventory(inventory)}>
                Export to Excel
              </Button>
            </Stack>
          </Stack>
          {canMarkLost && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Tick the items that are still missing and mark them Lost. A Lost item leaves the counts; the next scan that sees it brings it back and lists it as
              Found.
            </Typography>
          )}
          <Box sx={{ overflowX: 'auto' }}>
            <MuiTable size="small" sx={{ minWidth: 480 }}>
              <TableHead>
                <TableRow>
                  {canMarkLost && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={markableItems.length > 0 && markableItems.every((item) => selected.includes(item.item_id ?? ''))}
                        indeterminate={selected.length > 0 && selected.length < markableItems.length}
                        disabled={!markableItems.length}
                        onChange={(_, checked) => setSelected(checked ? markableItems.map((item) => item.item_id ?? '') : [])}
                        inputProps={{ 'aria-label': 'Select all missing items' }}
                      />
                    </TableCell>
                  )}
                  <TableCell>{grouping === 'category' ? 'Category / product / item' : 'Product / item'}</TableCell>
                  {columns.category && <TableCell>Category</TableCell>}
                  {columns.cameFrom && <TableCell>Came from</TableCell>}
                  {columns.reads && <TableCell align="right">Reads</TableCell>}
                  {columns.kpis.map((kpi) => (
                    <TableCell key={kpi.key} align="right">
                      {kpi.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columnCount}>
                      <Typography variant="body2" color="text.secondary">
                        {EMPTY[side]}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {groups.map((group) => [
                  groupRow(group, 2, grouping === 'category', true),
                  ...(open.includes(group.key)
                    ? grouping === 'category'
                      ? subgroups(group).flatMap((product) => [
                          groupRow(product, 5, false, false),
                          ...(open.includes(product.key) ? itemRows(product.items, 8) : []),
                        ])
                      : itemRows(group.items, 5)
                    : []),
                ])}
              </TableBody>
            </MuiTable>
          </Box>
        </Box>
      </Card>
      <ConfirmDialog
        open={confirmLost}
        onClose={() => setConfirmLost(false)}
        title={`Mark ${selected.length} ${selected.length === 1 ? 'item' : 'items'} as Lost?`}
        onConfirm={markLost}
        confirmLabel="Mark Lost"
        confirmColor="error"
        sending={marking}
      >
        They leave the counts but keep their history. The next scan that sees one brings it back where it was seen and lists it as Found.
      </ConfirmDialog>
    </>
  );
};

export default InventoryDetails;
